import {
  Children,
  type ReactElement,
  type ReactNode,
  useMemo,
  useState,
} from "react";

import { uniqueId } from "lodash-es";
import { useTracking } from "react-tracking";

import { type IBuiMenuProps } from "@btnet/blueprint-components/BuiMenu";
import {
  BuiOverflow,
  type BuiOverflowItemProps,
  type BuiOverflowMenuItemProps,
  type BuiOverflowMenuProps,
  type BuiOverflowProps,
} from "@btnet/blueprint-components/BuiOverflow";
import { useOverflowMenu } from "@btnet/blueprint-components/BuiOverflow/BuiOverflow.helpers";
import { useOverflowItemContent } from "@btnet/blueprint-components/BuiOverflow/BuiOverflow.helpers";
import { IconDotsThree } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { BTButton } from "../BTButton/BTButton";
import { BTDropdown, type IBTDropdownProps } from "../BTDropdown/BTDropdown";
import { BTMenu } from "../BTMenu/BTMenu";
import { BTPopover } from "../BTPopover/BTPopover";
import {
  BTSplitButtonOverflowItems,
  isBTSplitButtonElement,
} from "../BTSplitButton/BTSplitButton";

/**
 * BTOverflowProps defines the properties for the BTOverflow component, which is a wrapper around the BuiOverflow component from the Blueprint design system. It includes properties for testing, styling, and child elements, as well as all other props from Fluent UI's Overflow component.
 * - testid: string (required) - The test ID for the BTOverflow component, used for testing purposes.
 * - className: string (optional) - Additional class name(s) to apply to the BTOverflow component for styling purposes.
 * - style: React.CSSProperties (optional) - Additional inline styles to apply to the BTOverflow component for styling purposes.
 * - children: ReactNode (required) - The child elements to be rendered inside the BTOverflow component. This should typically be a collection of BTOverflowItem components.
 * - All other props from Fluent UI's Overflow component are also supported, such as padding for controlling the spacing between items.
 */
export type TBTOverflowProps = BuiOverflowProps;

/**
 * BTOverflowItemProps defines the properties for the BTOverflowItem component, which represents an individual item within the BTOverflow component. It includes properties for overflow content, child elements, and pinning or prioritization of items.
 * - overflowContent: ReactNode (optional) - The content to be displayed when the item is in the overflow menu. If not provided, the content will be the same as the children.
 * - children: ReactNode (required) - The child element to be rendered inside the BTOverflowItem component. This is the content that will be displayed in the main area of the BTOverflow component. If the item does not have overflowContent, the children will also be used as the content in the overflow menu.
 * - pinned: boolean (optional) - Indicates whether the item should be pinned to the main area of the BTOverflow component. If true, the item will always be visible in the main area and will not be moved to the overflow menu, regardless of available space. If false or not provided, the item may be moved to the overflow menu based on available space and priority.
 * - priority: number (optional) - Indicates the priority of the item for determining which items should be moved to the overflow menu when there is not enough space in the main area. Items with lower priority values will be moved to the overflow menu before items with higher priority values. If not provided, the default priority is 0.
 */
export type TBTOverflowItemProps = BuiOverflowItemProps;

/**
 * BTOverflowMenuProps defines the properties for the BTOverflowMenu component, which represents the menu that appears when items are moved to the overflow area. The ids property is an array of strings that represent the IDs of the items that should be included in the overflow menu.
 * - ids: string[] (required) - An array of strings that represent the IDs of the items that should be included in the overflow menu.
 */
export type TBTOverflowMenuProps = BuiOverflowMenuProps &
  IBuiMenuProps & {
    disabled?: boolean;
    tooltip?: ReactNode;
    /**
     * Accessible name for the "More" trigger. Set it when a page repeats the menu (one per row),
     * so each trigger says what it is for, e.g. "More actions for Pat Lee". The menu it opens
     * takes the same name, so each row's menu is told apart too.
     * @default "More" for the trigger, "More options" for the menu
     */
    triggerAriaLabel?: string;
  };
/**
 * BTOverflowMenuItemProps defines the properties for the BTOverflowMenuItem component, which represents an individual item within the overflow menu. The id property is a string that represents the unique identifier of the item, which is used to determine which items should be included in the overflow menu based on the ids provided to the BTOverflowMenu component.
 * - id: string (required) - The unique identifier of the item, which is used to determine which items should be included in the overflow menu based on the ids provided to the BTOverflowMenu component.
 */
export type TBTOverflowMenuItemProps = BuiOverflowMenuItemProps;

/**
 * The BTOverflow component is a wrapper around the BuiOverflow component from the Blueprint design system, providing additional functionality and styling specific to the Blueprint design system.
 * It allows for dynamic overflow of child elements based on available space, with support for pinned items and priority-based overflow.
 * @param props The props for the BTOverflow component, which include testid (a string for testing purposes), className (optional additional CSS class names), style (optional inline styles), children (the child elements to be rendered inside the BTOverflow component, typically a collection of BTOverflowItem components), and all other props from Fluent UI's Overflow component.
 * @returns A JSX element representing the BTOverflow component with the specified props.
 */
const BTOverflowInternal = (props: TBTOverflowProps) => {
  const { Track } = useTracking({
    element: "BTOverflow",
    uniqueId: props.testid,
  });
  return (
    <Track>
      <BuiOverflow
        {...props}
        overflowDirection={props.overflowDirection ?? "start"}
      />
    </Track>
  );
};

/**
 * The BTOverflow component is a wrapper around the BuiOverflow component from the Blueprint design system, providing additional functionality and styling specific to the Blueprint design system. It allows for dynamic overflow of child elements based on available space, with support for pinned items and priority-based overflow.
 * @param props The props for the BTOverflow component, which include testid (a string for testing purposes), className (optional additional CSS class names), style (optional inline styles), children (the child elements to be rendered inside the BTOverflow component, typically a collection of BTOverflowItem components), and all other props from Fluent UI's Overflow component.
 * @returns A JSX element representing the BTOverflow component with the specified props.
 */
const BTOverflowItem = (props: TBTOverflowItemProps) => {
  return <BuiOverflow.Item {...props} />;
};

/**
 * The BTOverflowMenuItem component represents an individual item within the overflow menu. It uses the useOverflowItemContent hook to determine if the item should be shown in the overflow menu and to retrieve the content for the menu item. If the item is not currently overflowing, it returns null and does not render anything. If the item is overflowing, it renders a BTMenu.Item with the appropriate content.
 * @param props The props for the BTOverflowMenuItem component, which include id (the unique identifier of the item) and any other props that should be passed to the BTMenu.Item component.
 * @returns A JSX element representing the BTOverflowMenuItem component, which is either a BTMenu.Item with the appropriate content if the item is currently overflowing, or null if the item is not overflowing.
 */
const BTOverflowMenuItem = (
  props: TBTOverflowMenuItemProps,
): ReactElement | null => {
  const { id } = props;
  const { showContent, content } = useOverflowItemContent(id);

  if (!showContent) {
    return null;
  }

  // A split button can't share one menu row between its two click targets, so it rolls up as
  // sibling rows (primary action, then each caret item) rather than nested in a single row.
  if (isBTSplitButtonElement(content)) {
    return <BTSplitButtonOverflowItems {...content.props} />;
  }

  return (
    <BTMenu.Item testid={id} eventKey={id}>
      {content}
    </BTMenu.Item>
  );
};

// By default antd only flips the menu when it would overflow the *document*, so on a list
// page taller than the viewport, the More trigger in the fixed footer opened its menu below
// the fold. `alwaysByViewport` checks against the viewport instead, as BdsButtonGroup's
// rollup did. rc-trigger merges `align` over the placement shallowly, so `adjustX`/`adjustY`
// must be restated, and the zero offset keeps BuiDropdown's default. antd 4's `Align` type
// predates `alwaysByViewport` (dom-align supports it), hence the separate object.
const viewportOverflow = {
  adjustX: true,
  adjustY: true,
  alwaysByViewport: true,
};
const overflowMenuAlign: NonNullable<IBTDropdownProps["align"]> = {
  offset: [0, 0],
  overflow: viewportOverflow,
};

/**
 * The BTOverflowMenu component represents the menu that appears when items are moved to the overflow area. It is a wrapper around the BTDropdown and BTMenu components, providing additional functionality for rendering the overflow menu based on the items that have been moved to the overflow area.
 * The ids prop is an array of strings that represent the IDs of the items that should be included in the overflow menu. The component uses the useOverflowMenu hook to determine if there are any items currently overflowing, and if so, it renders a dropdown menu with the corresponding menu items.
 * @param props  The props for the BTOverflowMenu component, which include ids (an array of strings representing the IDs of the items to be included in the overflow menu), children (optional additional content to be rendered in the overflow menu), testid (a string for testing purposes), className (optional additional CSS class names), and all other props from Antd's Menu component.
 * @returns  A JSX element representing the BTOverflowMenu component, which includes a dropdown trigger and a menu that displays the overflow items when there are items currently overflowing. If there are no items overflowing and no children content, the component returns null and does not render anything.
 */
const BTOverflowMenu = (props: TBTOverflowMenuProps) => {
  const {
    ids,
    children,
    testid,
    className,
    disabled,
    tooltip,
    triggerAriaLabel,
    ...rest
  } = props;
  const { ref: menuRef, isOverflowing } = useOverflowMenu<HTMLElement>();
  // iOS WKWebView only surfaces the portaled menu when the trigger carries the
  // menu-button ARIA relationship pointing at it (ado-288244).
  const menuId = useMemo(
    () => props.id ?? uniqueId("bt-overflow-menu-"),
    [props.id],
  );
  const [open, setOpen] = useState(false);

  const hasChildren = Children.toArray(children).some(Boolean);

  if (!isOverflowing && !hasChildren) {
    return null;
  }

  const trigger = (
    <span ref={menuRef}>
      <BTOverflowItem id="more" pinned>
        <BTPopover content={tooltip} trigger="hover" placement="top">
          <BTButton
            type="tertiary"
            data-testid="More"
            aria-label={triggerAriaLabel ?? "More"}
            aria-haspopup="menu"
            aria-expanded={disabled ? false : open}
            // Only while open: antd mounts the overlay lazily, so the id has
            // nothing to resolve to when closed.
            {...(!disabled && open ? { "aria-controls": menuId } : {})}
            {...(disabled === undefined ? {} : { disabled })}
            icon={<IconDotsThree />}
          />
        </BTPopover>
      </BTOverflowItem>
    </span>
  );

  if (disabled) {
    return trigger;
  }

  return (
    <BTDropdown
      testid={`${testid}-dropdown`}
      className="bt-overflow-dropdown"
      align={overflowMenuAlign}
      onVisibleChange={setOpen}
      overlay={
        // `bt-overflow-menu` is not a styling hook - the nested-button menu-row rules live in
        // ../BTMenu/BTMenu.less. It stays because it is a documented Playwright selector
        // (see btnet-e2e-tests/docs/architecture/components/bds-rollup-menu.md).
        <BTMenu
          {...rest}
          id={menuId}
          testid={testid}
          className={cn("bt-overflow-menu", className)}
          aria-label={triggerAriaLabel ?? "More options"}
        >
          {isOverflowing &&
            ids?.map((id: string) => {
              return <BTOverflowMenuItem key={id} id={id} />;
            })}
          {children}
        </BTMenu>
      }
      trigger={["click"]}
    >
      {trigger}
    </BTDropdown>
  );
};

/**
 * BTOverflow is the main component for handling overflow of child elements. It is an object that combines the BTOverflowInternal component with a static Item property that references the BTOverflowItem component and a static Menu property that references the BTOverflowMenu component. This allows for a convenient API for rendering an overflow menu with items that can be pinned or prioritized based on available space.
 * - testid: string (required) - The test ID for the BTOverflow component, used for testing purposes.
 * - className: string (optional) - Additional class name(s) to apply to the BTOverflow component for styling purposes.
 * - style: React.CSSProperties (optional) - Additional inline styles to apply to the BTOverflow component for styling purposes.
 * - children: ReactNode (required) - The child elements to be rendered inside the BTOverflow component. This should typically be a collection of BTOverflowItem components.
 * - All other props from Fluent UI's Overflow component are also supported, such as padding for controlling the spacing between items.
 *
 * Example usage:
 * ```
 * <BTOverflow testid="my-overflow">
 *   <BTOverflow.Menu ids={["item1", "item2", "item3"]} testid="my-overflow-menu" />
 *   <BTOverflow.Item id="item1">Item 1</BTOverflow.Item>
 *   <BTOverflow.Item id="item2">Item 2</BTOverflow.Item>
 *   <BTOverflow.Item id="item3">Item 3</BTOverflow.Item>
 * </BTOverflow>
 * ```
 *
 * In this example, the BTOverflow component will render three items. If there is not enough space to display all items, they will be moved to the overflow menu, which can be accessed by clicking the "More" button. The ids prop on the BTOverflow.Menu component should match the id props of the BTOverflow.Item components to ensure they are included in the overflow menu when necessary.
 */
export const BTOverflow = Object.assign(BTOverflowInternal, {
  /**
   * BTOverflowItem sub-component for individual overflow items.
   * A styled wrapper around Fluent UI OverflowItem with full access to its API.
   *
   * Props:
   * - overflowContent: ReactNode (optional) - The content to be displayed when the item is in the overflow menu. If not provided, the content will be the same as the children.
   * - children: ReactNode (required) - The child element to be rendered inside the BTOverflowItem component. This is the content that will be displayed in the main area of the BTOverflow component. If the item does not have overflowContent, the children will also be used as the content in the overflow menu.
   * - pinned: boolean (optional) - Indicates whether the item should be pinned to the main area of the BTOverflow component. If true, the item will always be visible in the main area and will not be moved to the overflow menu, regardless of available space. If false or not provided, the item may be moved to the overflow menu based on available space and priority.
   * - priority: number (optional) - Indicates the priority of the item for determining which items should be moved to the overflow menu when there is not enough space in the main area. Items with lower priority values will be moved to the overflow menu before items with higher priority values. If not provided, the default priority is 0.
   *
   * Example usage:
   * ```
   * <BTOverflow.Item id="item1" overflowContent="Item 1 in menu">Item 1</BTOverflow.Item>
   * ```
   */
  Item: BTOverflowItem,

  /**
   * BTOverflowMenu sub-component for rendering the overflow menu when items are moved to the overflow area.
   * Props:
   * - ids: string[] (required) - An array of strings that represent the IDs of the items that should be included in the overflow menu.
   * - children: ReactNode (optional) - Additional content to be rendered in the overflow menu, such as dividers or non-item content.
   * - All other props from Antd's Menu component are also supported.
   *
   * Example usage:
   * ```
   * <BTOverflow.Menu ids={["item1", "item2"]} testid="my-overflow-menu">
   *   <BTMenu.Item testid="custom-item">Custom Item</BTMenu.Item>
   * </BTOverflow.Menu>
   */
  Menu: BTOverflowMenu,
});
